import { useState } from "react";
import { User, Lock } from "lucide-react";
import useAuthStore from "../stores/useAuthStore";
import { profileApi } from "../api";
import Card from "../components/ui/Card";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";

const ROLE_LABELS = {
  admin: "Administrateur",
  expert_senior: "Expert Senior",
  expert_junior: "Expert Junior",
  assistante: "Assistante",
};

const ProfilePage = () => {
  const { user, updateUser } = useAuthStore();

  // ─── Profile form state ──────────────────────
  const [profileForm, setProfileForm] = useState({
    nom: user?.nom || "",
    prenom: user?.prenom || "",
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileSuccess, setProfileSuccess] = useState(null);

  // ─── Password form state ─────────────────────
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    new_password_confirm: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);

  // ─── Profile handlers ────────────────────────
  const handleProfileChange = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
    setProfileSuccess(null);
    setProfileError(null);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileError(null);
    setProfileSuccess(null);
    try {
      const updated = await profileApi.updateProfile(profileForm);
      updateUser(updated);
      setProfileSuccess("Profil mis à jour avec succès");
    } catch (err) {
      setProfileError(err.detail || err.message || "Erreur lors de la mise à jour");
    } finally {
      setProfileLoading(false);
    }
  };

  // ─── Password handlers ───────────────────────
  const handlePasswordChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    setPasswordSuccess(null);
    setPasswordError(null);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    if (passwordForm.new_password.length < 6) {
      setPasswordError("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (passwordForm.new_password !== passwordForm.new_password_confirm) {
      setPasswordError("Les mots de passe ne correspondent pas");
      return;
    }

    setPasswordLoading(true);
    try {
      await profileApi.changePassword(passwordForm);
      setPasswordSuccess("Mot de passe modifié avec succès");
      setPasswordForm({ current_password: "", new_password: "", new_password_confirm: "" });
    } catch (err) {
      setPasswordError(err.detail || err.message || "Erreur lors du changement de mot de passe");
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-carex-50 flex items-center justify-center">
          <User size={20} className="text-carex-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Mon profil</h1>
      </div>

      {/* ─── Informations personnelles ─────────── */}
      <Card title="Informations personnelles">
        <form onSubmit={handleProfileSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-slate-700">Email</label>
              <p className="text-sm text-slate-900 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                {user?.email}
              </p>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-slate-700">Rôle</label>
              <p className="text-sm text-slate-900 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                {ROLE_LABELS[user?.role] || user?.role}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Prénom"
              name="prenom"
              value={profileForm.prenom}
              onChange={(e) => handleProfileChange("prenom", e.target.value)}
              required
            />
            <Input
              label="Nom"
              name="nom"
              value={profileForm.nom}
              onChange={(e) => handleProfileChange("nom", e.target.value)}
              required
            />
          </div>

          {profileError && (
            <p className="text-sm text-red-600">{profileError}</p>
          )}
          {profileSuccess && (
            <p className="text-sm text-green-600">{profileSuccess}</p>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={profileLoading}>
              {profileLoading ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </form>
      </Card>

      {/* ─── Changer le mot de passe ──────────── */}
      <Card title="Changer le mot de passe">
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <Input
            label="Mot de passe actuel"
            name="current_password"
            type="password"
            value={passwordForm.current_password}
            onChange={(e) => handlePasswordChange("current_password", e.target.value)}
            required
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Nouveau mot de passe"
              name="new_password"
              type="password"
              value={passwordForm.new_password}
              onChange={(e) => handlePasswordChange("new_password", e.target.value)}
              required
            />
            <Input
              label="Confirmer le mot de passe"
              name="new_password_confirm"
              type="password"
              value={passwordForm.new_password_confirm}
              onChange={(e) => handlePasswordChange("new_password_confirm", e.target.value)}
              required
            />
          </div>

          {passwordError && (
            <p className="text-sm text-red-600">{passwordError}</p>
          )}
          {passwordSuccess && (
            <p className="text-sm text-green-600">{passwordSuccess}</p>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={passwordLoading}>
              <Lock size={16} />
              {passwordLoading ? "Modification..." : "Modifier le mot de passe"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ProfilePage;
